import {
  BaseDateChartData,
  SeriesInfo,
} from "../../../../components/charts/BaseDateDataChart/types";
import {
  ClusteredColumnChartDataItem,
  SeriesData,
} from "../../../../components/charts/ClusteredColumnChart/types";
import { CountryNameMap } from "../../../../hooks/useCountryNameMap";
import { SpendBreakdownResult } from "../../../../legacy/types/companySearch/MI";
import { isEqual } from "../../../../utils/jsUtils";
import {
  TAB_VIEW,
  TAB_VIEW_NAME_MAP,
} from "../../WorkspaceConfiguration/constants";
import {
  DEFAULT_API_EMPLOYEE_RANGES,
  DEFAULT_API_REVENUE_RANGES,
  UNKNOWN,
} from "../constants";
import {
  DetectionSegment,
  MinMaxSegment,
  SegmentProductDetection,
} from "./hooks/useProductDetectionsBySegment";
import { ProductTimeSeriesData } from "./hooks/useTrendingProductData";

export const getInsufficientInfoText = (segment: TAB_VIEW) => {
  return `Set up the ${TAB_VIEW_NAME_MAP[segment]} settings to get more detailed insights`;
};

export function getSegmentNameFromRange(
  segment: MinMaxSegment,
  ranges: { name: string; range: MinMaxSegment }[]
): string {
  return ranges.find((data) => isEqual(data.range, segment))?.name || UNKNOWN;
}

function getEmployeeSegmentName(segment: MinMaxSegment): string {
  return getSegmentNameFromRange(segment, DEFAULT_API_EMPLOYEE_RANGES);
}

function getRevenueSegmentName(segment: MinMaxSegment): string {
  return getSegmentNameFromRange(segment, DEFAULT_API_REVENUE_RANGES);
}
const countryReverseMapCache = new WeakMap<
  Map<string, { alpha2: string; id: string }>,
  Map<string, string>
>();

function getCountryNameFromAlpha2(
  alpha2Code: string,
  countryNameMap: Map<string, { alpha2: string; id: string }>
): string {
  // Get or create the reverse lookup map from the cache
  let reverseMap = countryReverseMapCache.get(countryNameMap);

  if (!reverseMap) {
    reverseMap = new Map<string, string>();

    countryNameMap.forEach((value, name) => {
      reverseMap!.set(value.alpha2, name);
    });
    countryReverseMapCache.set(countryNameMap, reverseMap);
  }

  // Return the country name or fall back to the code itself
  return reverseMap.get(alpha2Code) || alpha2Code;
}
interface ProductDetectionDataParams {
  productDetectionData: SegmentProductDetection[];
  productNameInfo: Record<number, string>;
  segment: DetectionSegment;
  countryNameMap: CountryNameMap;
  limitToTop?: number;
}

export function convertProductDetectionData({
  productDetectionData,
  productNameInfo,
  segment,
  countryNameMap,
}: ProductDetectionDataParams): ClusteredColumnChartDataItem[] {
  function getSegmentName(detection: SegmentProductDetection): string {
    if (segment === DetectionSegment.EMPLOYEE) {
      return getEmployeeSegmentName(detection.segment as MinMaxSegment);
    } else if (segment === DetectionSegment.REVENUE) {
      return getRevenueSegmentName(detection.segment as MinMaxSegment);
    } else if (segment === DetectionSegment.COUNTRY) {
      return getCountryNameFromAlpha2(
        detection.segment as string,
        countryNameMap
      );
    } else {
      return (detection.segment as string) || UNKNOWN;
    }
  }

  const groupedData: { [segment: string]: SeriesData[] } = {};

  for (const detection of productDetectionData) {
    const segmentName = getSegmentName(detection);

    if (segmentName !== UNKNOWN) {
      if (!groupedData[segmentName]) {
        groupedData[segmentName] = [];
      }

      groupedData[segmentName].push({
        name: productNameInfo[detection.productId] || UNKNOWN,
        value: detection.hits,
      });
    }
  }

  // Convert to chart format
  return Object.entries(groupedData).map(([category, cluster]) => {
    return {
      category,
      cluster: [...cluster].sort((a, b) => b.value - a.value),
      totalCompanyCount: cluster.reduce(
        (total, item) => total + (item.value || 0),
        0
      ),
    };
  });
}

export function mapSpendBreakdownResultToChartData(
  spendData: SpendBreakdownResult[]
) {
  return spendData
    .filter((item) => item.spend > 0 || item.companies > 0)
    .map((item) => ({
      category: item.dimension || "",
      barSeriesValue: item.spend,
      lineSeriesValue: item.companies,
    }));
}

export function convertProductTimeSeriesData(
  timeSeriesData: ProductTimeSeriesData[],
  productNameInfo: Record<number, string>
): BaseDateChartData[] {
  const groupedData: { [date: string]: SeriesInfo[] } = {};

  for (const entry of timeSeriesData) {
    if (!groupedData[entry.monthStartDate]) {
      groupedData[entry.monthStartDate] = [];
    }
    groupedData[entry.monthStartDate].push({
      name: productNameInfo[entry.productId] || UNKNOWN,
      value: entry.hits,
    });
  }

  const result: BaseDateChartData[] = [];
  for (const date in groupedData) {
    result.push({
      date,
      series: groupedData[date],
    });
  }

  return result;
}

export function sortDataBySpend(data: SpendBreakdownResult[]) {
  return [...data].sort((spendA, spendB) => {
    // Primary sort by spend
    const spendDiff = spendB.spend - spendA.spend;

    // If spend values are equal, use companies count as tiebreaker
    if (spendDiff === 0) {
      return spendB.companies - spendA.companies;
    }

    // Otherwise sort by spend
    return spendDiff;
  });
}
